import React, { useMemo } from "react";
import {
  FaClock,
  FaMapMarkerAlt,
  FaUser,
  FaEnvelope,
  FaPen,
  FaLock,
  FaClipboardList,
} from "react-icons/fa";

const EntriesTable = ({
  entries = [],
  selectedEntries = [],
  setSelectedEntries,
  onEditEntry,
}) => {
  const entryIds = useMemo(
    () =>
      entries
        .map((entry) => Number(entry.id))
        .filter((id) => Number.isSafeInteger(id) && id > 0),
    [entries]
  );

  const selectedIds = useMemo(
    () => new Set(selectedEntries.map(Number)),
    [selectedEntries]
  );

  const selectedCount = entryIds.filter((id) =>
    selectedIds.has(id)
  ).length;

  const allSelected =
    entryIds.length > 0 && selectedCount === entryIds.length;

  const someSelected = selectedCount > 0 && !allSelected;

  const toggleSelect = (id) => {
    if (!setSelectedEntries) return;

    const normalizedId = Number(id);

    if (!Number.isSafeInteger(normalizedId) || normalizedId <= 0) {
      return;
    }

    setSelectedEntries((previous) => {
      const ids = [...new Set(previous.map(Number))];

      return ids.includes(normalizedId)
        ? ids.filter((value) => value !== normalizedId)
        : [...ids, normalizedId];
    });
  };

  const toggleSelectAll = () => {
    if (!setSelectedEntries) return;

    setSelectedEntries((previous) => {
      const ids = [...new Set(previous.map(Number))];

      return allSelected
        ? ids.filter((id) => !entryIds.includes(id))
        : [...new Set([...ids, ...entryIds])];
    });
  };

  if (entries.length === 0) {
    return (
      <div className="qroof-entries-empty">
        <FaClipboardList />
        <h3>Keine Einträge vorhanden</h3>
        <p>Neue Einträge erscheinen hier als Karten.</p>
      </div>
    );
  }

  return (
    <div className="qroof-entries">
      <div className="qroof-entries-toolbar">
        <label className="qroof-select-all">
          <input
            type="checkbox"
            checked={allSelected}
            ref={(checkbox) => {
              if (checkbox) {
                checkbox.indeterminate = someSelected;
              }
            }}
            onChange={toggleSelectAll}
            disabled={!setSelectedEntries}
          />
          <span>Alle auswählen</span>
        </label>

        <span className="qroof-selection-count" aria-live="polite">
          {selectedCount > 0
            ? `${selectedCount} von ${entries.length} ausgewählt`
            : `${entries.length} Einträge`}
        </span>
      </div>

      <div className="qroof-entry-grid">
        {entries.map((entry) => {
          const entryId = Number(entry.id);
          const validEntryId =
            Number.isSafeInteger(entryId) && entryId > 0;
          const isSelected = selectedIds.has(entryId);

          const fullName = [
            entry.user_first_name,
            entry.user_last_name,
          ]
            .filter(Boolean)
            .join(" ")
            .trim();

          const displayName =
            fullName ||
            entry.user_email ||
            "Unbekannter Mitarbeiter";

          const status = entry.status || "erfasst";
          const knownStatus = [
            "erfasst",
            "gebucht",
            "archiviert",
          ].includes(status);

          const statusClass = knownStatus ? status : "unknown";
          const statusLabel =
            {
              erfasst: "Erfasst",
              gebucht: "Gebucht",
              archiviert: "Archiviert",
            }[status] || status;

          const canEdit = status === "erfasst";
          const date = formatDate(entry.time);

          return (
            <article
              key={entry.id}
              className={`qroof-entry-card ${
                isSelected ? "is-selected" : ""
              }`}
            >
              <div className="qroof-entry-header">
                <div className="qroof-entry-person">
                  <div className="qroof-entry-avatar">
                    <FaUser aria-hidden="true" />
                  </div>

                  <div className="qroof-entry-person-details">
                    <h3>{displayName}</h3>

                    {entry.user_email && (
                      <span className="qroof-entry-email">
                        <FaEnvelope aria-hidden="true" />
                        {entry.user_email}
                      </span>
                    )}
                  </div>
                </div>

                <input
                  className="qroof-entry-checkbox"
                  type="checkbox"
                  checked={isSelected}
                  disabled={!setSelectedEntries || !validEntryId}
                  onChange={() => toggleSelect(entryId)}
                  aria-label={`Eintrag ${entry.id} von ${displayName} auswählen`}
                />
              </div>

              <div className="qroof-entry-badges">
                <span
                  className={`qroof-entry-badge is-${statusClass}`}
                >
                  <span className="qroof-status-dot" />
                  {statusLabel}
                </span>

                {entry.edited_at && (
                  <span className="qroof-entry-badge is-edited">
                    <FaPen aria-hidden="true" />
                    Bearbeitet
                  </span>
                )}
              </div>

              <div className="qroof-entry-location">
                <div className="qroof-location-icon">
                  <FaMapMarkerAlt aria-hidden="true" />
                </div>

                <div>
                  <span className="qroof-entry-label">
                    Arbeitsort
                  </span>
                  <strong>
                    {entry.code || "Ohne Ortsangabe"}
                  </strong>
                </div>
              </div>

              <div className="qroof-entry-activity">
                <span className="qroof-entry-label">
                  Tätigkeit
                </span>

                <p>
                  {entry.activity || "Keine Tätigkeit angegeben"}
                </p>
              </div>

              <div className="qroof-entry-footer">
                <div className="qroof-entry-date">
                  <FaClock aria-hidden="true" />

                  <div>
                    <strong>{date.day}</strong>
                    {date.time && <span>{date.time} Uhr</span>}
                  </div>
                </div>

                <button
                  type="button"
                  className="qroof-entry-edit"
                  disabled={!canEdit || !onEditEntry}
                  title={
                    !canEdit
                      ? "Gebuchte und archivierte Einträge sind gesperrt"
                      : !onEditEntry
                        ? "Bearbeitung derzeit nicht verfügbar"
                        : "Eintrag bearbeiten"
                  }
                  onClick={() => onEditEntry?.(entry)}
                  aria-label={`Eintrag ${entry.id} ${
                    canEdit ? "bearbeiten" : "gesperrt"
                  }`}
                >
                  {canEdit ? <FaPen /> : <FaLock />}
                  <span>{canEdit ? "Bearbeiten" : "Gesperrt"}</span>
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
};

const formatDate = (value) => {
  if (!value) {
    return { day: "Kein Datum", time: "" };
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return { day: "Ungültiges Datum", time: "" };
  }

  return {
    day: date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }),
    time: date.toLocaleTimeString("de-DE", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
};

export default EntriesTable;